import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import useSeo from "@/lib/useSeo";
import { toast } from "sonner";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function Checkout() {
  const { items, subtotal, updateQty, removeItem, lineKey, clear } = useCart();
  const { settings } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [idem] = useState(uuid);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: user?.email || "", phone: "",
    street: "", house_number: "", postal_code: "", city: "", country: "Netherlands",
    company: "", vat_number: "",
  });
  useSeo({ title: "Checkout — ONUSA" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const threshold = settings.free_shipping_threshold || 150;
  const shipping = subtotal >= threshold ? 0 : (settings.shipping_flat_rate || 6.95);
  const total = subtotal + shipping;

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Je winkelmand is leeg");
    setSubmitting(true);
    const addr = { first_name: form.first_name, last_name: form.last_name, street: form.street,
      house_number: form.house_number, postal_code: form.postal_code, city: form.city,
      country: form.country, company: form.company || null, vat_number: form.vat_number || null };
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
        email: form.email, phone: form.phone, shipping_address: addr,
        discount_code: discountCode || null, idempotency_key: idem, shipping_method: "standard",
      });
      if (data.checkout_url) {
        clear();
        window.location.assign(data.checkout_url);
      } else {
        toast.success("Bestelling geplaatst");
        clear();
        navigate(`/checkout/return?order=${data.order_number}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return <div className="onusa-container py-32 text-center" data-testid="checkout-empty">
      <p className="heading-x text-2xl">JE WINKELMAND IS LEEG</p>
      <Link to="/shop" className="btn-x-outline mt-6">SHOP DE PROTOCOL</Link>
    </div>;
  }

  return (
    <div className="onusa-container py-10 md:py-16" data-testid="checkout-page">
      <h1 className="heading-x text-3xl md:text-4xl mb-10">AFREKENEN</h1>
      <div className="grid lg:grid-cols-[1fr_420px] gap-12">
        <form onSubmit={submit} className="space-y-8" data-testid="checkout-form">
          <div>
            <p className="label-x mb-4">CONTACT</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required type="email" placeholder="E-mail" value={form.email} onChange={set("email")} className="chk-input" data-testid="checkout-email" />
              <input required placeholder="Telefoon" value={form.phone} onChange={set("phone")} className="chk-input" data-testid="checkout-phone" />
            </div>
          </div>
          <div>
            <p className="label-x mb-4">VERZENDADRES</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Voornaam" value={form.first_name} onChange={set("first_name")} className="chk-input" data-testid="checkout-first-name" />
              <input required placeholder="Achternaam" value={form.last_name} onChange={set("last_name")} className="chk-input" data-testid="checkout-last-name" />
              <input required placeholder="Straat" value={form.street} onChange={set("street")} className="chk-input" data-testid="checkout-street" />
              <input required placeholder="Huisnummer" value={form.house_number} onChange={set("house_number")} className="chk-input" data-testid="checkout-house" />
              <input required placeholder="Postcode" value={form.postal_code} onChange={set("postal_code")} className="chk-input" data-testid="checkout-postal" />
              <input required placeholder="Plaats" value={form.city} onChange={set("city")} className="chk-input" data-testid="checkout-city" />
              <input required placeholder="Land" value={form.country} onChange={set("country")} className="chk-input" data-testid="checkout-country" />
            </div>
            <details className="mt-4">
              <summary className="label-x cursor-pointer">ZAKELIJKE GEGEVENS (OPTIONEEL)</summary>
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <input placeholder="Bedrijfsnaam" value={form.company} onChange={set("company")} className="chk-input" data-testid="checkout-company" />
                <input placeholder="Btw-nummer" value={form.vat_number} onChange={set("vat_number")} className="chk-input" data-testid="checkout-vat" />
              </div>
            </details>
          </div>
          <button type="submit" disabled={submitting} className="btn-x w-full" data-testid="checkout-submit">
            {submitting ? "VERWERKEN…" : `BETAAL €${total.toFixed(2)}`}
          </button>
          <p className="label-x text-center">VEILIG BETALEN VIA MOLLIE · BETALING SERVERSIDE BEVESTIGD</p>
        </form>

        <aside className="bg-white border border-[#E8E1D8] p-6 h-fit sticky top-24" data-testid="checkout-summary">
          <p className="label-x mb-5">BESTELOVERZICHT</p>
          <div className="space-y-4 max-h-72 overflow-y-auto no-scrollbar">
            {items.map((it) => {
              const key = lineKey(it);
              return (
                <div key={key} className="flex gap-3">
                  <div className="w-14 h-18 bg-[#E8E1D8] shrink-0 overflow-hidden"><img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" /></div>
                  <div className="flex-1 text-xs">
                    <p className="uppercase tracking-wide font-medium">{it.name}</p>
                    <p className="label-x mt-1">{it.size ? `MAAT ${it.size}` : ""}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="number" min="1" value={it.quantity} onChange={(e) => updateQty(key, parseInt(e.target.value) || 1)} className="w-12 border border-[#E8E1D8] px-1 py-0.5 text-xs" />
                      <button type="button" onClick={() => removeItem(key)} className="label-x">VERWIJDER</button>
                    </div>
                  </div>
                  <p className="font-mono text-xs">€{(it.price * it.quantity).toFixed(2)}</p>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-5">
            <input placeholder="KORTINGSCODE" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} className="flex-1 border border-[#E8E1D8] px-3 py-2 text-xs font-mono uppercase bg-white" data-testid="checkout-discount" />
          </div>
          <div className="rule-x mt-5 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#77736D]">Subtotaal</span><span className="font-mono">€{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#77736D]">Verzending</span><span className="font-mono">{shipping === 0 ? "GRATIS" : `€${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between text-base pt-2 border-t border-[#E8E1D8] mt-2"><span className="heading-x text-sm">TOTAAL</span><span className="font-mono" data-testid="checkout-total">€{total.toFixed(2)}</span></div>
            <p className="label-x">INCL. {settings.vat_rate || 21}% BTW</p>
          </div>
        </aside>
      </div>
      <style>{`.chk-input{width:100%;background:#fff;border:1px solid #E8E1D8;padding:0.7rem 0.9rem;font-size:0.875rem;outline:none}.chk-input:focus{border-color:#1D1D1B}`}</style>
    </div>
  );
}
